import { useCallback } from 'react';

// Helper function to handle API responses, reducing repetition
async function handleApiResponse(response, successMessage, errorMessagePrefix, showPopup, hidePopup) {
  if (response.ok) {
    const data = await response.json();
    if (data.success === false) { // Check for backend-reported failure even with 2xx status
        throw new Error(data.message || `${errorMessagePrefix}: Odpowiedź serwera wskazuje na błąd (success: false).`);
    }
    hidePopup(); // Hide loading popup
    if (successMessage) {
        showPopup({ message: successMessage, type: 'success' });
    }
    return data; // Return data on success
  } else {
    // Handle HTTP errors (non-2xx status)
    let errorData = { message: `${errorMessagePrefix}: Błąd serwera (status ${response.status})` };
    try {
      const parsedError = await response.json();
      errorData.message = parsedError.message || errorData.message;
      // Include interactionId if present in error response (for AI logs)
      if (parsedError.interactionId) {
          errorData.interactionId = parsedError.interactionId;
      }
    } catch (e) {
       // If parsing JSON fails, try to get text response
       try {
           const errorText = await response.text();
           errorData.message = errorText || errorData.message;
       } catch (textErr) { /* Ignore text reading error */ }
    }
    throw errorData; // Throw an object containing message and potentially interactionId
  }
}


export function useOrderDetailsActions(
    orderId,
    backendUrl,
    data, // { order, files, editedQuantities, guestsTextMap } from useOrderDetailsData
    uiState, // { showPopup, hidePopup, setCopySuccessMessage, setAiStateMap, setCurrentLogs, setLogsLoading, setDeletingFileId, setDuplicatingFileId, setAddingProjectFromTemplateId, setIsUploading, aiRequestText, handleShowMetadata } from useOrderDetailsUIState
    dataUpdaters, // { setFiles, setPdfMetadatas, setEditedQuantities, setOrder } from useOrderDetailsData
    navigate // from react-router-dom
) {

  const { order, files, editedQuantities, guestsTextMap } = data;
  const {
    showPopup, hidePopup, setCopySuccessMessage, setAiStateMap, setCurrentLogs, setLogsLoading,
    setDeletingFileId, setDuplicatingFileId, setAddingProjectFromTemplateId, setIsUploading,
    aiRequestText, handleShowMetadata: uiHandleShowMetadata // Rename to avoid conflict
  } = uiState;
  const { setFiles, setPdfMetadatas, setEditedQuantities, setOrder } = dataUpdaters;

  // --- File Upload ---
  const handleFileDropped = useCallback(async ({ base64, name }) => {
    setIsUploading(true);
    showPopup({ title: "Upload", message: "Przesyłanie pliku...", type: 'loading' });

    try {
      const base64data = base64.split(",")[1] || "";
      const binary = atob(base64data);
      const array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([array], { type: "application/octet-stream" });
      const formData = new FormData();
      formData.append("file", blob, name);

      const res = await fetch(`${backendUrl}/admin/orders/${orderId}/files`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      const result = await handleApiResponse(res, "Plik został dodany.", "Błąd podczas przesyłania pliku", showPopup, hidePopup);

      const addedFiles = result.files || [];
      setFiles(addedFiles);
      // Update quantities for new files
      const newQuantities = { ...editedQuantities };
      addedFiles.forEach(file => {
        if (!(file.id in newQuantities)) {
          newQuantities[file.id] = file.quantity ?? 1;
        }
      });
      setEditedQuantities(newQuantities);
      setPdfMetadatas(result.pdfMetadatas || []);

    } catch (err) {
      console.error("[handleFileDropped]", err);
      hidePopup(); // Ensure loading is hidden on error
      showPopup({ title: "Błąd uploadu", message: err.message || "Nie udało się dodać pliku.", type: 'error' });
    } finally {
      setIsUploading(false);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setIsUploading, setFiles, setPdfMetadatas, editedQuantities, setEditedQuantities]);

  // --- Show Metadata ---
   const handleShowMetadata = useCallback(async (downloadKey) => {
    showPopup({ title: "Metadane", message: "Pobieranie metadanych...", type: 'loading' });
    try {
      const metaUrl = `${backendUrl}/admin/orders/${orderId}/pdf-metadata?downloadKey=${downloadKey}`;
      const r = await fetch(metaUrl, { credentials: "include" });
      const data = await handleApiResponse(r, null, "Błąd pobierania metadanych PDF", showPopup, hidePopup); // No success message needed

      if (!data.pdfData) { // Specific check for pdfData presence
          throw new Error("Brak pdfData w odpowiedzi serwera.");
      }
      uiHandleShowMetadata(data.pdfData); // Call the UI state handler
      hidePopup(); // Hide loading popup manually as no success message is shown by handleApiResponse
    } catch (err) {
      console.error("[handleShowMetadata]", err);
      hidePopup();
      showPopup({ title: "Błąd metadanych", message: err.message || "Nie udało się pobrać metadanych PDF.", type: 'error' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, uiHandleShowMetadata]);


  // --- Generate Production PDF ---
  const handleGeneratePdf = useCallback((downloadKey) => {
    if (!downloadKey) {
      showPopup({ title: "Błąd", message: "Brak klucza do pobrania pliku (downloadKey).", type: 'error' });
      return;
    }
    // Confirmation popup removed based on previous logic, directly call doGeneratePdf
    doGeneratePdf(downloadKey);
  }, [showPopup]); // Removed doGeneratePdf from dependencies, defined below

  const doGeneratePdf = useCallback(async (downloadKey) => {
    showPopup({ title: "Generowanie PDF", message: "Proszę czekać...", type: 'loading' });
    const genUrl = `${backendUrl}/admin/orders/${orderId}/generate-pdf?downloadKey=${downloadKey}&production=1`;
    try {
      const res = await fetch(genUrl, { method: "POST", credentials: "include" });
      const data = await handleApiResponse(res, "PDF produkcyjny został wygenerowany.", "Błąd generowania PDF", showPopup, hidePopup);

      const updatedFiles = data.files || [];
      if (updatedFiles.length > 0) { // Check if files array is not empty
        setFiles(updatedFiles);
        // Update quantities
        const newQuantities = { ...editedQuantities };
        updatedFiles.forEach(file => {
          newQuantities[file.id] = file.quantity ?? 1;
        });
        setEditedQuantities(newQuantities);
      }
    } catch (err) {
      console.error("[doGeneratePdf]", err);
      hidePopup();
      showPopup({ title: "Błąd generowania PDF", message: err.message || "Nie udało się wygenerować nowego PDF produkcyjnego.", type: 'error' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setFiles, editedQuantities, setEditedQuantities]);


  // --- Preview PDF ---
  const handlePreviewPdf = useCallback(async (downloadKey) => {
    showPopup({ title: "Generowanie podglądu", message: "Proszę czekać...", type: 'loading' });
    const previewUrl = `${backendUrl}/admin/orders/${orderId}/preview-pdf?downloadKey=${downloadKey}`;
    try {
      const res = await fetch(previewUrl, { method: "GET", credentials: "include" });
      if (!res.ok) { // handleApiResponse is not suitable for blob responses
        throw new Error(`Błąd generowania podglądu (status=${res.status})`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "podglad.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      hidePopup(); // Hide loading popup on success
    } catch (err) {
      console.error("[handlePreviewPdf]", err);
      hidePopup();
      showPopup({ title: "Błąd podglądu", message: err.message || "Nie udało się wygenerować podglądu PDF.", type: 'error' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup]);

  // --- Save File Changes (Status/Quantity) ---
  const handleSaveFileChanges = useCallback(async (fileId) => {
    const fileToSave = files.find(f => f.id === fileId);
    if (!fileToSave) return;

    let quantityToSave = editedQuantities[fileId];
    if (quantityToSave === "") {
      quantityToSave = 0;
    } else {
      quantityToSave = parseInt(quantityToSave, 10);
      if (isNaN(quantityToSave) || quantityToSave < 0) {
        quantityToSave = fileToSave.quantity ?? 1;
        setEditedQuantities(prev => ({ ...prev, [fileId]: quantityToSave })); // Correct UI state
      }
    }

    const updatedFileObject = { ...fileToSave, quantity: quantityToSave };
    const filesPayload = files.map(f => (f.id === fileId ? updatedFileObject : f));

    // Use a silent loading state or skip popup for frequent saves? For now, keep it.
    showPopup({ title: "Zapisywanie zmian", message: "Proszę czekać...", type: 'loading' });

    const body = {
      orderName: order?.orderName,
      customOrderId: order?.email,
      files: filesPayload
    };

    try {
      const res = await fetch(`${backendUrl}/admin/orders/${orderId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      // Use handleApiResponse, but suppress the success message for less noise
      const result = await handleApiResponse(res, null, "Błąd zapisu zmian pliku", showPopup, hidePopup);

      const refreshedFiles = result.order?.files || files; // Get the updated files array from the response
      setFiles(refreshedFiles); // Update the main files state

      // Reset editedQuantities based entirely on the refreshed data from the server
      // This ensures the UI reflects the actual saved state.
      setEditedQuantities(() => {
          const newQuantities = {};
          refreshedFiles.forEach(file => {
              newQuantities[file.id] = file.quantity ?? 1;
          });
          return newQuantities;
      });


    } catch (err) {
      console.error("[handleSaveFileChanges]", err);
      hidePopup();
      showPopup({ title: "Błąd zapisu", message: err.message || "Nie udało się zapisać zmian.", type: 'error' });
    }
  }, [files, editedQuantities, order, backendUrl, orderId, showPopup, hidePopup, setFiles, setEditedQuantities]);

  // --- Handle Status Change (Updates local state and triggers save) ---
   const handleStatusChange = useCallback((fileId, newStatusId) => {
    const newFiles = files.map((f) => {
      if (f.id === fileId) {
        const currentStatusId = f.statusId ?? "";
        const targetStatusId = newStatusId ? String(newStatusId) : "";
        if (currentStatusId !== targetStatusId) {
          return { ...f, statusId: newStatusId ? Number(newStatusId) : null };
        }
      }
      return f;
    });

    if (JSON.stringify(newFiles) !== JSON.stringify(files)) {
        setFiles(newFiles); // Update local state first
        handleSaveFileChanges(fileId); // Then trigger save
    }
  }, [files, setFiles, handleSaveFileChanges]);


  // --- Guest List Actions ---
  const handleFetchGuestsForFile = useCallback(async (fileObj) => {
    const projectId = fileObj.id;
    if (!projectId) {
      showPopup({ title: "Błąd", message: "Brak ID projektu (file.id) – nie można pobrać listy gości.", type: 'error' });
      return;
    }
    uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: true }));
    const url = `${backendUrl}/admin/projects/${projectId}/history/latest`;
    try {
      const r = await fetch(url, { credentials: "include" });
      // Don't use handleApiResponse here as success doesn't need a popup
      if (!r.ok) throw new Error(`Błąd pobierania JSON (status=${r.status}).`);
      const data = await r.json();
      if (!data.jsonData) throw new Error("Brak jsonData w odpowiedzi.");

      let personalizacja = [];
      const pages = data.jsonData.pages || [];
      let personalizePageIndex = pages.findIndex(pg =>
        (pg.texts || []).some(t => t.flags?.personalize) ||
        (pg.images || []).some(im => im.flags?.personalize) ||
        (pg.svgs || []).some(sv => sv.flags?.personalize)
      );

      if (personalizePageIndex >= 0 && Array.isArray(pages[personalizePageIndex].personalizacja)) {
        personalizacja = pages[personalizePageIndex].personalizacja;
      } else {
         console.warn(`[handleFetchGuestsForFile][Admin] No page with personalize flag or no 'personalizacja' array found.`);
      }
      uiState.setGuestsTextMap((prev) => ({ ...prev, [projectId]: personalizacja.join("\n") }));
    } catch (err) {
      console.error("[handleFetchGuestsForFile]", err);
      showPopup({ title: "Błąd pobierania gości", message: err.message || "Nie udało się pobrać listy gości.", type: 'error' });
    } finally {
      uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: false }));
    }
  }, [backendUrl, showPopup, uiState]); // Added uiState dependency

  const handleSaveGuestsForFile = useCallback(async (fileObj) => {
    const projectId = fileObj.id;
    if (!projectId) {
      showPopup({ title: "Błąd", message: "Brak ID projektu (file.id) – nie można zapisać listy gości.", type: 'error' });
      return;
    }
    uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: true }));
    showPopup({ title: "Zapisywanie listy gości", message: "Proszę czekać...", type: 'loading' });

    try {
      // 1. Fetch latest history
      const getUrl = `${backendUrl}/admin/projects/${projectId}/history/latest`;
      const getRes = await fetch(getUrl, { credentials: "include" });
      if (!getRes.ok) throw new Error(`Błąd pobierania JSON (status=${getRes.status}).`);
      const getData = await getRes.json();
      if (!getData.jsonData) throw new Error("Brak jsonData (przed zapisem).");

      // 2. Prepare updated pages data
      let pages = getData.jsonData.pages || [];
      const lines = (guestsTextMap[projectId] || "").split("\n").map(l => l.trim()).filter(Boolean);
      let personalizePageIndex = pages.findIndex(pg =>
        (pg.texts || []).some(t => t.flags?.personalize) ||
        (pg.images || []).some(im => im.flags?.personalize) ||
        (pg.svgs || []).some(sv => sv.flags?.personalize)
      );

      if (personalizePageIndex >= 0) {
        pages = pages.map((pg, idx) => idx === personalizePageIndex ? { ...pg, personalizacja: lines } : pg);
      } else {
        console.error("[handleSaveGuestsForFile][Admin] Could not find page with personalize flag. Save failed.");
        throw new Error("Nie znaleziono strony z flagą personalizacji w projekcie.");
      }

      // 3. Save updated history
      const finalJson = { pages };
      const saveUrl = `${backendUrl}/admin/projects/${projectId}/history`;
      const saveRes = await fetch(saveUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonData: finalJson, saveType: "manual" })
      });

      // Use handleApiResponse for saving part
      await handleApiResponse(saveRes, "Lista gości została zapisana.", "Błąd zapisu JSON", showPopup, hidePopup);

    } catch (err) {
      console.error("[handleSaveGuestsForFile]", err);
      hidePopup(); // Ensure loading is hidden
      showPopup({ title: "Błąd zapisu gości", message: err.message || "Nie udało się zapisać listy gości.", type: 'error' });
    } finally {
      uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: false }));
    }
  }, [backendUrl, guestsTextMap, showPopup, hidePopup, uiState]); // Added uiState dependency

  // --- Generate Personalized Project ---
  const handleGeneratePersonalizedProject = useCallback((fileObj) => {
    const existingChild = files.find((f) => f.parentFileId === fileObj.id);
    if (existingChild) {
      showPopup({
        title: "Uwaga!",
        message: <>Projekt z listą gości już istnieje (<strong>{existingChild.fileName}</strong>).<br />Czy chcesz go nadpisać? Poprzedni plik zostanie usunięty.</>,
        type: 'confirm',
        confirmText: 'Tak, nadpisz',
        onConfirm: () => doGeneratePersonalized(fileObj, existingChild),
      });
    } else {
      doGeneratePersonalized(fileObj, null);
    }
  }, [files, showPopup]); // Removed doGeneratePersonalized dependency

  const doGeneratePersonalized = useCallback(async (fileObj, existingChild) => {
    showPopup({ title: "Generowanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/personalize?downloadKey=${fileObj.downloadKey}`;
      const body = { parentFileId: fileObj.id, childFileId: existingChild?.id || null };
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      const data = await handleApiResponse(res, "Wygenerowano projekt z listą gości.", "Błąd generowania projektu z listą gości", showPopup, hidePopup);

      if (data.files) {
        const updatedFiles = data.files || [];
        setFiles(updatedFiles);
        const newQuantities = { ...editedQuantities };
        updatedFiles.forEach(file => { newQuantities[file.id] = file.quantity ?? 1; });
        setEditedQuantities(newQuantities);
      }
      if (data.pdfMetadatas) {
        setPdfMetadatas(data.pdfMetadatas);
      }
    } catch (err) {
      console.error("[doGeneratePersonalized]", err);
      hidePopup();
      showPopup({ title: "Błąd generowania", message: err.message || "Nie udało się wygenerować projektu spersonalizowanego.", type: 'error' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setFiles, editedQuantities, setEditedQuantities, setPdfMetadatas]);

  // --- Delete Project ---
  // Define doDeleteProject BEFORE handleDeleteProject
  const doDeleteProject = useCallback(async (fileObj) => {
    if (!fileObj) return;
    console.log(`[doDeleteProject] Starting deletion for file: ${fileObj.fileName} (ID: ${fileObj.id})`); // Debug log
    setDeletingFileId(fileObj.id);
    showPopup({ title: "Usuwanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/projects/${fileObj.id}`;
      console.log(`[doDeleteProject] Sending DELETE request to: ${url}`); // Debug log
      const res = await fetch(url, { method: "DELETE", credentials: "include" });
      await handleApiResponse(res, "Projekt został pomyślnie usunięty.", "Błąd usuwania projektu (admin)", showPopup, hidePopup);

      // Update local state on success
      setFiles(prevFiles => prevFiles.filter(f => f.id !== fileObj.id));
      setEditedQuantities(prev => {
        const newState = { ...prev };
        delete newState[fileObj.id];
        return newState;
      });

    } catch (err) {
      console.error("[doDeleteProject][admin]", err);
      hidePopup();
      showPopup({ title: "Błąd usuwania", message: err.message || "Wystąpił błąd podczas próby usunięcia projektu.", type: 'error' });
    } finally {
      setDeletingFileId(null);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setDeletingFileId, setFiles, setEditedQuantities]);

  const handleDeleteProject = useCallback((fileObj) => {
    if (!fileObj) return;
    console.log(`[handleDeleteProject] Called for file: ${fileObj.fileName} (ID: ${fileObj.id})`); // Debug log
    const confirmCallback = () => {
        console.log(`[handleDeleteProject] onConfirm called for file: ${fileObj.fileName}`); // Debug log
        doDeleteProject(fileObj); // Now doDeleteProject is guaranteed to be defined
    };
    showPopup({
      title: "Potwierdzenie usunięcia",
      message: <>Czy na pewno chcesz trwale usunąć projekt <strong>{fileObj.fileName}</strong>? Tej operacji nie można cofnąć.</>,
      type: 'confirm',
      confirmText: 'Tak, usuń',
      onConfirm: confirmCallback, // Use the wrapped callback
      onCancel: () => console.log(`[handleDeleteProject] Delete cancelled for file: ${fileObj.fileName}`) // Optional: log cancellation
    });
  }, [showPopup, doDeleteProject]); // Keep doDeleteProject dependency

  // --- Duplicate Project ---
  const handleDuplicateProject = useCallback(async (fileObj) => {
    if (!fileObj) return;
    setDuplicatingFileId(fileObj.id);
    showPopup({ title: "Duplikowanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/duplicate-project`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileId: fileObj.id })
      });
      const result = await handleApiResponse(res, "Projekt został pomyślnie zduplikowany.", "Błąd duplikowania projektu (admin)", showPopup, hidePopup);

      const updatedFiles = result.files || [];
      setFiles(updatedFiles);
      const newQuantities = { ...editedQuantities };
      updatedFiles.forEach(file => { if (!(file.id in newQuantities)) newQuantities[file.id] = file.quantity ?? 1; });
      setEditedQuantities(newQuantities);

    } catch (err) {
      console.error("[handleDuplicateProject][admin]", err);
      hidePopup();
      showPopup({ title: "Błąd duplikowania", message: err.message || "Wystąpił błąd podczas próby duplikowania projektu.", type: 'error' });
    } finally {
      setDuplicatingFileId(null);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setDuplicatingFileId, setFiles, editedQuantities, setEditedQuantities]);

  // --- Add Project From Template ---
  const handleAddProjectFromTemplate = useCallback(async (templateId) => {
    if (!templateId) return;
    setAddingProjectFromTemplateId(templateId);
    showPopup({ title: "Dodawanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/add-project-from-template`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId })
      });
      const result = await handleApiResponse(res, "Projekt został pomyślnie dodany z szablonu.", "Błąd dodawania projektu z szablonu (admin)", showPopup, hidePopup);

      const updatedFiles = result.files || [];
      setFiles(updatedFiles);
      const newQuantities = { ...editedQuantities };
      updatedFiles.forEach(file => { if (!(file.id in newQuantities)) newQuantities[file.id] = file.quantity ?? 1; });
      setEditedQuantities(newQuantities);

    } catch (err) {
      console.error("[handleAddProjectFromTemplate][admin]", err);
      hidePopup();
      showPopup({ title: "Błąd dodawania", message: err.message || "Wystąpił błąd podczas próby dodania projektu z szablonu.", type: 'error' });
    } finally {
      setAddingProjectFromTemplateId(null);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setAddingProjectFromTemplateId, setFiles, editedQuantities, setEditedQuantities]);

  // --- Save Email ---
  const handleSaveEmail = useCallback(async (newEmail) => {
    if (!order || newEmail === order.email) {
      uiState.setIsEditingEmail(false); // Just close edit mode if no change
      return;
    }
    showPopup({ title: "Zapisywanie emaila", message: "Proszę czekać...", type: 'loading' });
    const body = { customOrderId: newEmail }; // Assuming customOrderId is the email field
    try {
      const res = await fetch(`${backendUrl}/admin/orders/${orderId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      await handleApiResponse(res, "Adres e-mail został zaktualizowany.", "Błąd zapisu emaila", showPopup, hidePopup);

      // Update local state on success
      setOrder(prevOrder => ({ ...prevOrder, email: newEmail }));
      uiState.setEditedEmail(newEmail); // Sync UI state
      uiState.setIsEditingEmail(false);

    } catch (err) {
      console.error("[handleSaveEmail]", err);
      hidePopup();
      showPopup({ title: "Błąd zapisu", message: err.message || "Nie udało się zapisać adresu e-mail.", type: 'error' });
      // Don't close edit mode on error
    }
  }, [order, orderId, backendUrl, showPopup, hidePopup, setOrder, uiState]); // Added uiState dependency

  // --- Copy Public Link ---
  const handleCopyPublicLink = useCallback(() => {
    if (!order || !order.publicAccessKey) {
      showPopup({ title: "Brak klucza", message: "Nie można skopiować linku, brak klucza publicznego.", type: 'error' });
      return;
    }
    const publicLink = `${window.location.origin}/client/orders/${order.publicAccessKey}`;
    console.log(`[handleCopyPublicLink] Attempting to copy: ${publicLink}`); // Debug log
    console.log(`[handleCopyPublicLink] navigator.clipboard:`, navigator.clipboard); // Detailed log
    console.log(`[handleCopyPublicLink] typeof navigator.clipboard.writeText:`, typeof navigator.clipboard?.writeText); // Detailed log

    // Check Clipboard API availability more robustly
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      console.log("[handleCopyPublicLink] Using Clipboard API (navigator.clipboard.writeText)"); // Debug log
      navigator.clipboard.writeText(publicLink)
        .then(() => {
          console.log("[handleCopyPublicLink] Clipboard API success."); // Debug log
          setCopySuccessMessage("Link skopiowany!");
          setTimeout(() => setCopySuccessMessage(""), 2000);
        })
        .catch(err => {
          console.error('[handleCopyPublicLink] Clipboard API error: ', err); // Debug log
          showPopup({ title: "Błąd kopiowania", message: "Nie udało się skopiować linku (API).", type: 'error' });
        });
    } else {
      // Fallback for older browsers or insecure contexts
      console.warn("[handleCopyPublicLink] Clipboard API not available or writeText is not a function, using fallback execCommand."); // Updated log
      try {
        const textArea = document.createElement("textarea");
        textArea.value = publicLink;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          console.log("[handleCopyPublicLink] Fallback execCommand success."); // Debug log
          setCopySuccessMessage("Link skopiowany!");
          setTimeout(() => setCopySuccessMessage(""), 2000);
        } else {
          console.error('[handleCopyPublicLink] Fallback execCommand returned false.'); // Debug log
          throw new Error('document.execCommand("copy") returned false');
        }
      } catch (err) {
        console.error('[handleCopyPublicLink] Fallback execCommand error: ', err); // Debug log
        showPopup({ title: "Błąd kopiowania", message: "Nie udało się skopiować linku (fallback).", type: 'error' });
      }
    }
  }, [order, showPopup, setCopySuccessMessage]);


  // --- AI Actions ---
  const handleAiRequestSubmit = useCallback(async (projectId) => {
    if (!projectId || !aiRequestText.trim()) return;

    setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'loading', message: 'Przetwarzanie przez AI...', interactionId: null } }));
    uiState.handleCloseAiModal(); // Close modal via UI state handler

    try {
      const url = `${backendUrl}/admin/projects/${projectId}/ai-modify`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_request_text: aiRequestText })
      });

      // Use handleApiResponse, it throws an error object on failure, potentially with interactionId
      const data = await handleApiResponse(res, null, "Błąd Komunikacji AI", showPopup, hidePopup); // Suppress default success message

      // Handle specific success/failure cases based on backend response structure
      if (data.aiResponseType === 'text') {
        console.warn("[handleAiRequestSubmit] AI responded with text.");
        setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'error', message: data.message, interactionId: data.interactionId } }));
        showPopup({ title: "Nietypowa odpowiedź AI", message: data.message, type: 'error' });
      } else { // Assuming success means aiResponseType is not 'text' and no error was thrown by handleApiResponse
        console.log("[handleAiRequestSubmit] AI reported success.");
        setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'success', message: data.message || 'Modyfikacja AI zakończona sukcesem.', interactionId: data.interactionId } }));
        // Optionally show success popup or refresh data
        // showPopup({ message: data.message || 'Modyfikacja AI zakończona sukcesem.', type: 'success' });
        // Consider refreshing order data if AI modifies files: dataUpdaters.fetchOrderData();
      }

    } catch (err) { // Catch errors thrown by handleApiResponse or fetch itself
      console.error("[handleAiRequestSubmit] Error:", err);
      hidePopup(); // Ensure loading is hidden
      setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'error', message: err.message || 'Błąd przetwarzania AI.', interactionId: err.interactionId || null } })); // Use interactionId from error if available
      showPopup({ title: "Błąd AI", message: err.message || 'Nie udało się przetworzyć żądania AI.', type: 'error' });
    }
  }, [backendUrl, aiRequestText, setAiStateMap, showPopup, hidePopup, uiState]); // Added uiState dependency

  const handleFetchAiLogs = useCallback(async (projectId, interactionId) => {
    if (!interactionId) return;
    // Loading state is set in uiState.handleShowLogs
    setCurrentLogs("");
    try {
      const url = `${backendUrl}/admin/projects/${projectId}/ai-interactions/${interactionId}/log`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) { // Cannot use handleApiResponse for text response
        const errorData = await res.text();
        throw new Error(errorData || `Błąd pobierania logów (status ${res.status})`);
      }
      const logs = await res.text();
      setCurrentLogs(logs);
    } catch (err) {
      console.error("[handleFetchAiLogs] Error fetching logs:", err);
      setCurrentLogs(`Nie udało się pobrać logów: ${err.message}`);
      showPopup({ title: "Błąd logów", message: `Nie udało się pobrać logów: ${err.message}`, type: 'error' });
    } finally {
      setLogsLoading(false); // Reset loading state here
    }
  }, [backendUrl, setCurrentLogs, setLogsLoading, showPopup]);

  // --- Add Missing Font ---
  const handleAddMissingFont = useCallback(async (fontName, fontFile) => {
      if (!fontName || !fontFile) return;

      showPopup({ title: "Dodawanie czcionki", message: "Przesyłanie...", type: 'loading' });
      const formData = new FormData();
      formData.append("fontName", fontName);
      formData.append("fontFile", fontFile);

      try {
          const res = await fetch(`${backendUrl}/admin/fonts`, {
              method: "POST",
              credentials: "include",
              body: formData
          });
          await handleApiResponse(res, "Czcionka została dodana.", "Błąd podczas dodawania czcionki", showPopup, hidePopup);
          // Refresh font list after adding
          dataUpdaters.fetchAllFonts();
          // Reset UI state for adding font (handled in component or UI hook)
          uiState.setMissingFontToAdd(null);
          uiState.setMissingFontFile(null);
      } catch (err) {
          console.error("[handleAddMissingFont]", err);
          hidePopup();
          showPopup({ title: "Błąd dodawania czcionki", message: err.message || "Nie udało się dodać czcionki.", type: 'error' });
      }
  }, [backendUrl, showPopup, hidePopup, dataUpdaters, uiState]); // Added dataUpdaters and uiState


  // --- Navigation ---
  const handleNavigateToEditor = useCallback((downloadKey) => {
      navigate(`/admin/orders/${orderId}/pdf-editor/${downloadKey}`);
  }, [navigate, orderId]);


  return {
    handleFileDropped,
    handleShowMetadata,
    handleGeneratePdf,
    handlePreviewPdf,
    handleSaveFileChanges,
    handleStatusChange,
    handleFetchGuestsForFile,
    handleSaveGuestsForFile,
    handleGeneratePersonalizedProject,
    handleDuplicateProject,
    handleAddProjectFromTemplate,
    handleDeleteProject,
    handleSaveEmail,
    handleCopyPublicLink,
    handleAiRequestSubmit,
    handleFetchAiLogs,
    handleAddMissingFont,
    handleNavigateToEditor,
  };
}
